import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Analytics, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { StateService, UIRouterGlobals } from '@uirouter/core';
import { StoreAction } from 'store/actions';
import { PlanSwitchOrderReview } from 'store/plan-eligibility/models/order-review';
import { CmsPlanSwitch } from 'store/cms/models';
import { XmcPlanSwitchService } from 'my-account/activity/shared/services/xmc-plan-switch.service';

/**
 * Represents the component for plan switch order review.
 * This component is responsible for displaying the initial page data and handling user interactions.
 */
@Component({
    selector: 'xm-plan-switch-review-order',
    styleUrls: ['./plan-switch-review-order.scss'],
    templateUrl: './plan-switch-review-order.html',
    standalone: false
})
export class XmPlanSwitchReviewOrder implements OnInit, OnDestroy {
    public pageData: CmsLevelMoveStartData = {
        header: '',
        planGroupInfo: []
    };
    public cmsData: PlanSwitchOrderReview;
    public pageTitle: string;
    public upgradeBtn: string;
    public backBtn: string;
    public selectedPlans: BillPreviewPayload[];
    public lineId: string;
    public planCode: string;
    public isLoading: boolean = true;
    public expandIconOptions: MediaImageOptions;

    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];
    private state: StateService;
    private globalRouterState: UIRouterGlobals;
    private analytics: Analytics;
    private xmcPlanSwitchService: XmcPlanSwitchService;

    constructor(xmStore: XmStore, state: StateService, globalRouterState: UIRouterGlobals, analytics: Analytics, xmcPlanSwitchService: XmcPlanSwitchService) {
        Object.assign(this, { xmStore, state, globalRouterState, analytics, xmcPlanSwitchService });
    }

    public ngOnInit(): void {
        // Get line_id and plan_code from route parameters
        this.lineId = this.globalRouterState.params.line_id;
        this.planCode = this.globalRouterState.params.plan_code;
        
        // Create selectedPlans array with route parameters
        this.selectedPlans = [{
            lineId: this.lineId,
            newPlanId: this.getPlanCodeFromParams(this.planCode)
        }];
        const xmcPayloadBillPreview: object | undefined = this.xmcPlanSwitchService.getxmcConsentPayloadBillPreview();
        const selectedLineIdKeys: string[] = this.xmcPlanSwitchService.getSelectedLineIdKeys();
        if (xmcPayloadBillPreview && selectedLineIdKeys.length > 0) {
            this.xmStore.persist<PlanSwitchOrderReview>(StoreAction.POST_PLAN_SWITCH_ORDER_REVIEW, { ...xmcPayloadBillPreview, ...(selectedLineIdKeys.length > 0 && { is_downgrade: true })})
                .then((result: PlanSwitchOrderReview) => {
                    this.cmsData = result;
                    this.isLoading = false;
                    this.pageTitle = this.cmsData.orderSummary.pageHeader;
                    this.backBtn = this.cmsData.orderSummary.ctas.backCta;
                    this.upgradeBtn = this.cmsData.orderSummary.ctas.confirmCta;
                    this.sendAnalyticsData();
                })
                .catch(() => {
                    this.startUpgrade();
                });
        } else {
            this.xmStore.persist<PlanSwitchOrderReview>(StoreAction.POST_PLAN_SWITCH_ORDER_REVIEW, { changes: this.selectedPlans })
                .then((result: PlanSwitchOrderReview) => {
                    this.cmsData = result;
                    this.isLoading = false;
                    this.pageTitle = this.cmsData.orderSummary.pageHeader;
                    this.backBtn = this.cmsData.orderSummary.ctas.backCta;
                    this.upgradeBtn = this.cmsData.orderSummary.ctas.confirmCta;
                    this.sendAnalyticsData();
                })
                .catch(() => {
                    this.startUpgrade();
                });
        }
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (result: CmsPlanSwitch) => {
            this.expandIconOptions = {
                mobile: Cloudinary.generateMediaFromCms(result.expandIcon)
            };
        }));
    }

    /**
     * Cancels the level move and navigates to the landing page.
     */
    public onBack(): void {
        this.state.go(this.globalRouterState.params.backButtonPath, { line_id: this.lineId, plan_code: this.planCode });
    }

    /**
     * Initiates the level move process.
     */
    public startUpgrade(): void {
        this.state.go('account.activity.details.plan-switch.in-progress', { line_id: this.lineId, plan_code: this.planCode });
        
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    /**
     * Sends analytics data for the review order page.
     */
    private sendAnalyticsData(): void {
        if (!this.cmsData?.analyticsData) {
            return;
        }

        const productInfo: object = {
            previousPlan: this.cmsData.analyticsData.previousPlan || '',
            newPlan: this.cmsData.analyticsData.newPlan || '',
            changeType: this.cmsData.analyticsData.changeType || '',
            priceChanged: this.cmsData.analyticsData.priceChanged || '0'
        };

        this.analytics.sendCustomEvent('plan-change-review', {
            cart: {
                products: {
                    productInfo: [productInfo]
                }
            }
        }, true);
    }

    /**
     * Retrun only plancode from route parameters.
     */
    private getPlanCodeFromParams(planCode: string): string {
        return planCode?.includes(':') ? planCode?.split(':')[0] : planCode;
    }
}
